import type { JSX, ReactNode } from 'react';
import type {
	FieldValues,
	UseFormProps,
} from 'react-hook-form';
import type {
	ErrorSummaryLabels,
	FocusRef,
	ScrollRef,
	SetErrorSummaryLabel,
	SetFocusRef,
	SetScrollRef,
} from './FormContext';
import type { ZodObject, ZodRawShape } from 'zod';

import { zodResolver } from '@hookform/resolvers/zod';
import React, { useRef, useState } from 'react';
import {
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { FormProvider } from './FormContext';
import {
	type GetFieldTypeParams,
	getFieldType,
} from './getFieldType';
import { getRequiredGenerator } from './getRequiredGenerator';
import { generateDefaultValues } from './generateDefaultValues';


interface FormProps<
	TSchema extends FieldValues,
	TContext = unknown,
> {
	children: ReactNode;
	onSubmit?: SubmitHandler<TSchema>;
	useFormProps?: UseFormProps<TSchema, TContext>;
	zodSchema: ZodObject<ZodRawShape>;
}


export function Form<
	TSchema extends FieldValues,
	TContext = unknown,
>({
	children,
	onSubmit = (data) => console.debug(data),
	useFormProps,
	zodSchema,
	...otherProps
}: FormProps<TSchema, TContext>): JSX.Element {

	// TODO: useState can cause infinite re-render, convert to useRef, like focus and scroll.
	const [errorSummaryLabels, setErrorSummaryLabels] = useState<ErrorSummaryLabels>({});
	const setErrorSummaryLabel: SetErrorSummaryLabel = ({field, label}) => setErrorSummaryLabels(prev => ({
		...prev,
		[field]: label,
	}));

	const focusRefs = useRef<Record<string, FocusRef>>({});
	const scrollRefs = useRef<Record<string, ScrollRef>>({});

	const setFocusRef: SetFocusRef = ({ field, el }) => {
		focusRefs.current[field] = el;
	};

	const setScrollRef: SetScrollRef = ({ field, el }) => {
		scrollRefs.current[field] = el;
	};

	const emptyValues = zodSchema ? generateDefaultValues(zodSchema) : {};

	const {
		defaultValues = emptyValues as UseFormProps<TSchema, TContext>['defaultValues'],
		...otherUseFormProps
	} = useFormProps || {};

	const useFormMethods = useForm<TSchema, TContext>({
		...otherUseFormProps,
		defaultValues,
		resolver: zodResolver(zodSchema),
	});
	const {
		handleSubmit,
	} = useFormMethods;
	return (
		<FormProvider
			value={{
				...useFormMethods,
				defaultValues,
				emptyValues,
				errorSummaryLabels,
				focusRefs: focusRefs.current,
				getRequired: getRequiredGenerator(zodSchema),
				getFieldType: ({
					fieldName,
				}: {
					fieldName: GetFieldTypeParams['fieldName'];
				}) => getFieldType({
					fieldName,
					zodSchema,
				}),
				scrollRefs: scrollRefs.current,
				setErrorSummaryLabel,
				setFocusRef,
				setScrollRef,
				zodSchema,
			}}
		>
			<form
				{...otherProps}
				onSubmit={(event) => void handleSubmit(onSubmit)(event)}
			>
				{children}
			</form>
		</FormProvider>
	);
}
